import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Ticket, Users, Popcorn, Coins, AlertTriangle, Clock, Film, PlusCircle, ScanLine, Monitor, ShoppingBag, MonitorPlay } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [countdown, setCountdown] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => setStats({}));
    const t = setInterval(() => api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!stats?.today_showtime) return;
    const st = stats.today_showtime;
    const target = new Date(`${st.date}T${st.start_time}:00`);
    const tick = () => {
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) return setCountdown("läuft");
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h > 0 ? h + "h " : ""}${m}m ${s.toString().padStart(2, "0")}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [stats?.today_showtime]);

  if (!stats) return <div className="text-[#A8A398]">Lade Übersicht …</div>;

  const st = stats.today_showtime;
  const movie = st?.movie;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Willkommen zurück</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-1">Übersicht</h1>
      </div>

      {st && movie ? (
        <div className="relative overflow-hidden rounded-2xl border border-[#2A2A2A] bg-[#0A0A0A]">
          {movie.backdrop_url && (
            <img src={movie.backdrop_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
          <div className="relative p-6 md:p-10 grid md:grid-cols-[180px_1fr_260px] gap-6 items-center">
            {movie.poster_url ? (
              <img src={movie.poster_url} alt={movie.title_de} className="w-32 md:w-44 aspect-[2/3] object-cover rounded-lg shadow-2xl" />
            ) : (
              <div className="w-32 md:w-44 aspect-[2/3] bg-[#1A1A1A] rounded-lg flex items-center justify-center text-[#A8A398]"><Film /></div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Heute im Programm</div>
              <div className="font-heading text-3xl md:text-4xl mt-2">{movie.title_de}</div>
              <div className="text-[#A8A398] text-sm mt-1">{movie.genre} · {movie.duration_min} Min · {movie.rating}</div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Info label="Einlass" value={st.entry_time} />
                <Info label="Filmstart" value={st.start_time} />
                <Info label="Saal" value={st.hall} />
                <Info label="Plätze frei" value={stats.seats_available} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#A8A398]">Countdown</div>
              <div className="font-mono text-3xl md:text-4xl text-[#D4AF37] mt-1" data-testid="countdown">{countdown || "—"}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card-lux p-8 text-[#A8A398]">Heute ist keine Vorstellung geplant. Lege in Vorstellungen eine neue Vorführung an.</div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={Ticket} label="Verkaufte Tickets" value={stats.tickets_sold} />
        <Stat icon={Users} label="Reservierungen" value={stats.tickets_reserved} />
        <Stat icon={ScanLine} label="Eingecheckt" value={stats.checked_in} />
        <Stat icon={Popcorn} label="Offene Snacks" value={stats.open_snack_orders} />
        <Stat icon={Coins} label="Ticketumsatz" value={`${(stats.ticket_revenue || 0).toFixed(2)} €`} />
        <Stat icon={Coins} label="Gesamt" value={`${(stats.total_revenue || 0).toFixed(2)} €`} gold />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-lux p-6">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h3 className="font-heading text-xl">Schnellaktionen</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Action to="/admin/showtimes" icon={Clock} label="Neue Vorstellung" />
            <Action to="/admin/pos" icon={Coins} label="Ticket verkaufen" />
            <Action to="/kiosk" icon={MonitorPlay} label="Ticketautomat öffnen" newTab />
            <Action to="/admin/scanner" icon={ScanLine} label="Gast einchecken" />
            <Action to="/admin/snackbar" icon={Popcorn} label="Snackbestellung" />
            <Action to="/admin/movies" icon={Film} label="Film hinzufügen" />
            <Action to="/admin/displays" icon={Monitor} label="Displays" />
          </div>
        </div>
        <div className="card-lux p-6">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h3 className="font-heading text-xl">Lagerwarnungen</h3>
            <AlertTriangle size={16} className="text-[#D4AF37]" />
          </div>
          {stats.low_stock?.length ? (
            <div className="space-y-2">
              {stats.low_stock.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-[#1A1A1A] py-2 text-sm">
                  <div>{p.name}</div>
                  <div className="font-mono text-xs"><span className="text-[#EE9CA1]">{p.stock}</span> / {p.min_stock}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[#A8A398] text-sm">Alle Bestände in Ordnung.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#A8A398]">{label}</div>
      <div className="font-mono text-lg text-[#F5F0E6]">{value}</div>
    </div>
  );
}
function Stat({ icon: Icon, label, value, gold }) {
  return (
    <div className={`card-lux p-4 ${gold ? "border-[#D4AF37]/40" : ""}`}>
      <div className="flex items-center gap-2 text-[#A8A398] text-xs uppercase tracking-widest">
        <Icon size={13} /> {label}
      </div>
      <div className={`mt-2 text-2xl font-heading ${gold ? "text-[#D4AF37]" : ""}`}>{value}</div>
    </div>
  );
}
function Action({ to, icon: Icon, label, newTab }) {
  const cls = "flex flex-col items-center justify-center gap-2 py-4 rounded-lg border border-[#2A2A2A] hover:border-[#D4AF37] hover:bg-[#121212] transitions-safe";
  if (newTab) {
    return (
      <a href={to} target="_blank" rel="noreferrer" className={cls}>
        <Icon size={18} className="text-[#D4AF37]" />
        <span className="text-xs uppercase tracking-widest text-[#A8A398]">{label}</span>
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      <Icon size={18} className="text-[#D4AF37]" />
      <span className="text-xs uppercase tracking-widest text-[#A8A398]">{label}</span>
    </Link>
  );
}
